import {
  Table,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  HStack,
  Text,
  Portal,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { MoreVertical, Eye, Trash2 } from 'lucide-react'
import type { Residence } from '../types'
import { PropertyStats } from './PropertyStats'

interface PropertyTableProps {
  properties: Residence[]
  selectedPropertyId?: string | null
  onPropertyClick?: (id: string) => void
  onDelete?: (id: string) => void
  canDelete?: (id: string) => boolean
}

export function PropertyTable({
  properties,
  selectedPropertyId,
  onPropertyClick,
  onDelete,
  canDelete,
}: PropertyTableProps) {
  const { t } = useTranslation()

  return (
    <Table.Root variant="outline" size="sm">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>{t('properties.fields.key')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('properties.fields.name')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('properties.fields.city')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('properties.fields.postal_code')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('common.statistics')}</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center">{t('common.actions')}</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {properties.map((property) => {
          const isSelected = selectedPropertyId === property.id
          return (
            <Table.Row
              key={property.id}
              data-property-row
              bg={isSelected ? 'brand.50' : 'transparent'}
              _hover={{ bg: isSelected ? 'brand.100' : 'gray.50' }}
              cursor="pointer"
              onClick={() => onPropertyClick?.(property.id)}
              transition="background-color 0.2s"
            >
              <Table.Cell>
                <Text fontSize="xs" color="gray.500" fontFamily="mono">
                  {property.residence_key || property.id.slice(0, 8)}
                </Text>
              </Table.Cell>
              <Table.Cell fontWeight={isSelected ? 'semibold' : 'medium'}>
                {property.name}
              </Table.Cell>
              <Table.Cell>{property.city}</Table.Cell>
              <Table.Cell>{property.postal_code}</Table.Cell>
              <Table.Cell>
                <PropertyStats
                  residenceId={property.id}
                  onUnitsClick={() => console.log('Navigate to units')}
                  onOwnersClick={() => console.log('Navigate to owners')}
                />
              </Table.Cell>
              <Table.Cell onClick={(e) => e.stopPropagation()}>
                <HStack justify="center">
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.actions')}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          <MenuItem
                            value="details"
                            onClick={(e) => {
                              e.stopPropagation()
                              onPropertyClick?.(property.id)
                            }}
                          >
                            <HStack gap={2}>
                              <Eye size={16} />
                              <Text>{t('common.details')}</Text>
                            </HStack>
                          </MenuItem>
                          {onDelete && canDelete?.(property.id) && (
                            <MenuItem
                              value="delete"
                              onClick={(e) => {
                                e.stopPropagation()
                                onDelete(property.id)
                              }}
                              color="red.600"
                              _hover={{ bg: 'red.50' }}
                            >
                              <HStack gap={2}>
                                <Trash2 size={16} />
                                <Text>{t('common.delete')}</Text>
                              </HStack>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </HStack>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}
